import { useState, type FormEvent, type ReactNode } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { useMutation } from "@tanstack/react-query"
import {
    recoveryCall,
    recoveryEnabled,
    recoveryPath,
    initialRecoveryToken,
} from "@/api/recovery"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { APP_ROUTES } from "@/common/urls"

function RecoveryLayout({
    title,
    children,
}: {
    title: string
    children: ReactNode
}) {
    return (
        <div className="grid place-items-center bg-auth-pattern px-4 py-12 min-h-[70vh]">
            <section className="bg-white shadow-lg border rounded-lg p-6 sm:p-10 w-full max-w-xl space-y-6">
                <img
                    src="/CMHT_logo_large.svg"
                    alt="CMHT"
                    className="max-w-64 mx-auto"
                />
                <h1 className="text-2xl font-bold text-app-danger">{title}</h1>
                {children}
                <Link
                    className="block underline text-app-defaultLight"
                    to={APP_ROUTES.AUTH.LOGIN}
                >
                    Back to sign in
                </Link>
            </section>
        </div>
    )
}

export default function AuthRecovery() {
    const submit = useMutation({
        mutationFn: (input: Record<string, string>) =>
            recoveryCall("requestAccountRecovery", input),
    })
    const onSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        const form = new FormData(event.currentTarget)
        submit.mutate(
            Object.fromEntries(
                [...form.entries()].map(([key, value]) => [key, String(value)])
            )
        )
    }
    return (
        <RecoveryLayout title="Recover organisation access">
            {!recoveryEnabled ? (
                <p>
                    Account recovery is not available here yet. Please contact
                    CMHT support.
                </p>
            ) : submit.isSuccess ? (
                <p role="status">
                    If the details match an eligible organisation account, we
                    will send the next steps to the replacement address. Check
                    your inbox and spam folder. Requests expire after seven
                    days. Contact CMHT support if you need help.
                </p>
            ) : (
                <>
                    <p className="text-sm">
                        Use this form when you cannot access your organisation’s
                        primary email. We will verify the replacement inbox and
                        your authority to represent the organisation before an
                        administrator approves access.
                    </p>
                    <form onSubmit={onSubmit} className="space-y-4">
                        {(
                            [
                                [
                                    "originalEmail",
                                    "Existing account email",
                                    "email",
                                ],
                                [
                                    "firstName",
                                    "Replacement representative’s first name",
                                    "text",
                                ],
                                [
                                    "lastName",
                                    "Replacement representative’s last name",
                                    "text",
                                ],
                                [
                                    "replacementEmail",
                                    "Replacement email address",
                                    "email",
                                ],
                            ] as const
                        ).map(([name, label, type]) => (
                            <div className="space-y-2" key={name}>
                                <Label htmlFor={name}>{label}</Label>
                                <Input
                                    id={name}
                                    name={name}
                                    type={type}
                                    required
                                    maxLength={type === "email" ? 254 : 200}
                                    autoComplete="off"
                                />
                            </div>
                        ))}
                        <p className="text-sm">
                            If a verified recovery contact is enrolled, they
                            must confirm your request. Otherwise CMHT will
                            contact the organisation independently and obtain
                            written authority. A charity number alone is not
                            sufficient.
                        </p>
                        {submit.isError && (
                            <p role="alert" className="text-destructive">
                                We could not submit the request. Please try
                                again later or contact CMHT support.
                            </p>
                        )}
                        <Button disabled={submit.isPending} type="submit">
                            {submit.isPending
                                ? "Submitting…"
                                : "Request recovery"}
                        </Button>
                    </form>
                </>
            )}
        </RecoveryLayout>
    )
}

export function ConfirmRecovery() {
    const [params] = useSearchParams()
    const purpose = params.get("purpose") || ""
    const [token] = useState(initialRecoveryToken)
    const confirmation = useMutation({
        mutationFn: () =>
            recoveryCall("confirmRecoveryToken", { token, purpose }),
    })
    const descriptions: Record<string, string> = {
        contact:
            "Confirm that you control this inbox and agree to act as an authorised recovery contact for the organisation named in your email.",
        replacement:
            "Confirm that you control this replacement inbox. This does not yet grant account access. An administrator must approve the recovery.",
        secondary:
            "Confirm only if the representative and replacement email shown in your email are authorised by your organisation. This explicitly supports their recovery request.",
    }
    return (
        <RecoveryLayout title="Confirm recovery details">
            {confirmation.isSuccess ? (
                <p role="status">
                    Confirmation recorded. You can close this page. Recovery
                    requests still require administrator approval.
                </p>
            ) : !token || !descriptions[purpose] ? (
                <p role="alert">
                    This link is incomplete. Open the full link in your email.
                </p>
            ) : (
                <>
                    <p>{descriptions[purpose]}</p>
                    <p className="text-sm">
                        Links expire after 24 hours and work once. Only continue
                        if you recognise this request.
                    </p>
                    {confirmation.isError && (
                        <p role="alert" className="text-destructive">
                            This link is expired, replaced or already used, or
                            confirmation is temporarily unavailable. Contact
                            CMHT support for a new link.
                        </p>
                    )}
                    <Button
                        disabled={confirmation.isPending}
                        onClick={() => confirmation.mutate()}
                    >
                        {confirmation.isPending ? "Confirming…" : "Confirm"}
                    </Button>
                </>
            )}
        </RecoveryLayout>
    )
}

export function RecoveryLink() {
    return recoveryEnabled ? (
        <Link
            className="block text-sm underline text-app-defaultLight"
            to={recoveryPath}
        >
            I no longer have access to my email
        </Link>
    ) : null
}
